/**
 * Normalised authentication failures.
 *
 * Every code is also a key under `auth.errors.*` in the locale files, and the
 * UI branches on the code — never on a message. The API answers in Spanish but
 * its wording is not a UI contract, and MSAL's messages are English and carry
 * correlation ids and request state that have no business on a screen or in a
 * log line (PO-TI-022-00).
 */
import { ApiError } from '../api/client.js'

/**
 * The single source of the code list, so the union and the runtime guard below
 * cannot drift apart. The first six mirror the API's own `AuthErrorCode`
 * (`apps/api/src/auth/entra.types.ts`); the rest are produced in the browser,
 * where the API never gets a chance to answer.
 */
export const AUTH_ERROR_CODES = [
  /** No Authorization header reached the API. */
  'TOKEN_MISSING',
  /** Signature, issuer or audience did not verify. */
  'TOKEN_INVALID',
  /** Verified, but past `exp`. */
  'TOKEN_EXPIRED',
  /** Verified, but without the claims provisioning needs. */
  'TOKEN_PAYLOAD_INVALID',
  /** A real Entra account, from someone else's tenant. */
  'TENANT_FORBIDDEN',
  /** The directory email already belongs to another Vulcan user row (409). */
  'IDENTITY_EMAIL_CONFLICT',
  /** MSAL cannot renew silently: consent, MFA, or the Entra session ended. */
  'INTERACTION_REQUIRED',
  /** The redirect back from Entra carried an error instead of a code. */
  'REDIRECT_FAILED',
  /** The person declined or abandoned the Entra screen. */
  'USER_CANCELLED',
  /** `VITE_AZURE_TENANT_ID` / `VITE_AZURE_CLIENT_ID` are unset in this build. */
  'NOT_CONFIGURED',
  /** Signed in, but `GET /api/me` did not answer; the session stays read-only. */
  'ME_UNAVAILABLE',
] as const

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number]

/**
 * Codes that no amount of renewing can fix: the credential itself is fine, so
 * the identity behind it is the problem. The session is dropped and MSAL's
 * cached account with it, because signing in again with the same account would
 * land on exactly the same answer.
 */
export const TERMINAL_AUTH_ERROR_CODES: readonly AuthErrorCode[] = [
  'TENANT_FORBIDDEN',
  'IDENTITY_EMAIL_CONFLICT',
]

export class AuthError extends Error {
  constructor(
    readonly code: AuthErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'AuthError'
  }
}

function isAuthErrorCode(value: unknown): value is AuthErrorCode {
  return typeof value === 'string' && (AUTH_ERROR_CODES as readonly string[]).includes(value)
}

/**
 * The code behind a failure, or `null` when it is not an authentication one.
 *
 * Only `code` is read out of the API's `{ statusCode, error, code, message }`
 * body, and an unrecognised code is reported as no code at all: an untranslated
 * string rendered on a Spanish screen is worse than the generic message.
 */
export function authErrorCode(cause: unknown): AuthErrorCode | null {
  if (cause instanceof AuthError) return cause.code
  if (cause instanceof ApiError && typeof cause.body === 'object' && cause.body !== null) {
    const { code } = cause.body as Record<string, unknown>
    if (isAuthErrorCode(code)) return code
  }
  return null
}
